/**
 * Resolves the `environment` label attached to every Sentry event (#3252).
 *
 * WHAT WAS ACTUALLY BROKEN — narrower than #3252's issue text claims. Read
 * this before "simplifying" the vercel- prefix away.
 *
 * @sentry/nextjs 10.44 already defaults `environment` itself, but ONLY on two
 * of the three runtimes:
 *   client/index.js:69  environment: options.environment || SENTRY_ENVIRONMENT || getVercelEnv(true)  || NODE_ENV
 *   server/index.js:141 environment: options.environment || SENTRY_ENVIRONMENT || getVercelEnv(false) || NODE_ENV
 *   edge/index.js:72    environment: options.environment || SENTRY_ENVIRONMENT
 * and `getVercelEnv` returns `vercel-${VERCEL_ENV}` — so browser and server
 * events have been arriving as "vercel-production" / "vercel-preview" all
 * along. The EDGE runtime has no Vercel default and falls through to Sentry's
 * own "production" default, which is the runtime that genuinely could not tell
 * a preview deploy from real production.
 *
 * THE PREFIX IS LOAD-BEARING. Emitting bare "production"/"preview" here would
 * silently RENAME the two live buckets that existing Sentry alert rules, saved
 * searches and dashboards match on — alerting would go quiet with no error and
 * issue history would split across two names. This helper therefore mirrors
 * `getVercelEnv` byte-for-byte, including passing through any future VERCEL_ENV
 * value unvalidated, and exists to make all four inits explicit and consistent
 * rather than to change what production and preview are called.
 *
 * Both env var literals are spelled out because Next.js inlines `process.env.X`
 * by textual substitution — a computed or destructured lookup would silently
 * resolve to undefined in the client bundle. `NEXT_PUBLIC_VERCEL_ENV` is the
 * only one readable from the browser.
 */

export function resolveSentryEnvironment(): string {
  // Same precedence as the SDK: an explicit SENTRY_ENVIRONMENT override wins,
  // so the escape hatch for tagging a one-off deploy keeps working.
  const explicit = process.env.SENTRY_ENVIRONMENT;
  if (explicit) return explicit;

  // ORDER IS LOAD-BEARING — VERCEL_ENV FIRST. The SDK reads VERCEL_ENV on the
  // server and NEXT_PUBLIC_VERCEL_ENV on the client, and the two can disagree:
  // NEXT_PUBLIC_* is inlined at BUILD time, VERCEL_ENV is read at RUNTIME, so a
  // preview deployment promoted to production (or an instant rollback) carries
  // a stale build-time "preview" while running as production. Reading the
  // public one first would put every server error from the live site into
  // vercel-preview and out of production alerting. This order is equivalent to
  // the SDK on BOTH runtimes: in the browser bundle VERCEL_ENV is undefined, so
  // it falls through to the public one.
  const vercelEnv =
    process.env.VERCEL_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV;
  if (vercelEnv) return `vercel-${vercelEnv}`;

  // Off Vercel entirely. Outside a production build we are demonstrably local.
  if (process.env.NODE_ENV !== 'production') return 'development';

  // A production build with no Vercel signal cannot be told apart from a
  // preview build with no Vercel signal. Report "unknown" rather than guessing
  // "production" — this is the one place we deliberately diverge from the SDK
  // (which falls back to NODE_ENV), because a mislabelled preview error in the
  // production bucket is the exact defect this helper exists to prevent. On
  // Vercel this branch is unreachable: NEXT_PUBLIC_VERCEL_ENV is inlined into
  // the production browser bundle.
  return 'unknown';
}
